import { useCallback, useContext, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useRecoilValue } from 'recoil';
import createCachedSelector from 're-reselect';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import * as api from '../../services/api';
import useOptimisticMutation from '../useOptimisticMutation';
import useConvertTimeRange from '../useConvertTimeRange';
import * as sharedAtoms from '../../atoms/shared';
import { SLOT_FIELDS, TIMESHEET_DATA_KEY_OPTIONS } from '../../constants';
import { withOptimisticAdminReportMethod } from '../../utils/reportMethod';
import useAccount from './useAccount';
import useBranches from './useBranches';
import { PROJECT_EXTERNAL_UNBOOKINGS_KEY } from './useProjectExternalUnbookings';
import { TIMESHEET_PAYROLL_FIELDS_QUERY_KEY } from './useTimesheetPayrollFields';

const selector = createCachedSelector(
  (data) => data,
  (data, returnNullInCaseNoData) => returnNullInCaseNoData,
  (data, returnNullInCaseNoData, mappedBranches) => mappedBranches,
  (data, returnNullInCaseNoData, mappedBranches, storeKey, lockApprovedJobs) => lockApprovedJobs,
  (data, returnNullInCaseNoData, mappedBranches, lockApprovedJobs) => {
    // add each employee the default working days
    const formattedData =
      data || !returnNullInCaseNoData
        ? {
            ...data,
            employees: (data?.employees || []).map((employee) => ({
              ...employee,
              workDays: employee?.workDays?.map?.(({ branchId, days }) => ({
                branchId,
                days: days ?? mappedBranches[branchId]?.timesheetConfig?.workDays
              }))
            })),
            // Tag leave rows so usePayrollProcessAndQuery routes them to leaveRecordIds[].
            leave: (data?.leave || []).map((record) => ({ ...record, isLeave: true })),
            ...(data?.jobs && {
              jobs: data.jobs.map((job) => ({
                ...job,
                isJob: true,
                ...(lockApprovedJobs && job?.timesheet?.approved && { isLocked: true })
              }))
            })
          }
        : null;

    return { formattedData };
  }
)({
  keySelector: (data, returnNullInCaseNoData, mappedBranches, storeKey) => `${storeKey.join('#')}`
});

export const getTimesheetStoreKey = ({
  accountId,
  branchIds,
  timeRange,
  projectIds,
  employeeIds,
  filters,
  withFields
}) => ['timesheet', accountId, branchIds, timeRange, ...projectIds, ...employeeIds, filters, withFields];

const useTimeSheet = ({
  timeRange,
  filters,
  refetchPayrollOnSuccess,
  refetchProjectOnSuccess,
  withFields,
  employeeIds = [],
  projectIds = [],
  enabled = true,
  warnings = true,
  withJobs = false,
  withPto = false,
  withLeave = false,
  returnNullInCaseNoData = false,
  withExternalEmployees = false,
  withTimesheetFields = false,
  invoiceStatus,
  onSuccess
}) => {
  const { accountId } = useAccount();
  const { enablePayrollProcessJobsFeature } = useContext(ConfigContext);
  const queryClient = useQueryClient();

  const branchIds = useRecoilValue(sharedAtoms.selectedBranches);

  const timeRangeValue = useConvertTimeRange(timeRange);
  const { mappedBranches } = useBranches();

  const isBreakTimesDisplayed = branchIds?.some(
    (branch) => mappedBranches[branch]?.timesheetConfig.isBreakTimesDisplayed
  );
  const showEmployeeSignoffColumn = branchIds?.some(
    (branch) => mappedBranches[branch]?.timesheetConfig.requireEmployeeTimesheetSignoff
  );

  const showClientSignoffColumn = branchIds?.some(
    (branch) => mappedBranches[branch]?.timesheetConfig.requireClientTimesheetSignoff
  );

  const timeRangeValueExistsAndValid = useMemo(
    () =>
      timeRangeValue &&
      timeRangeValue.from &&
      timeRangeValue.to &&
      timeRangeValue.from !== 'Invalid date' &&
      timeRangeValue.to !== 'Invalid date',
    [timeRangeValue]
  );

  const storeKey = getTimesheetStoreKey({
    accountId,
    branchIds,
    timeRange,
    projectIds,
    employeeIds,
    filters,
    withFields
  });

  const { isLoading, data, refetch, isFetching } = useQuery(
    storeKey,
    () =>
      api.fetchTimeSheet({
        accountId,
        branchIds,
        ...timeRangeValue,
        employeeIds,
        projectIds,
        filters,
        warnings,
        withFields,
        withExternalEmployees,
        withTimesheetFields,
        withJobs,
        withPto,
        withLeave,
        invoiceStatus
      }),
    {
      staleTime: 0,
      enabled: enabled && !!timeRangeValueExistsAndValid,
      onSuccess,
      select: (timesheetData) =>
        selector(timesheetData, returnNullInCaseNoData, mappedBranches, storeKey, enablePayrollProcessJobsFeature)
    }
  );

  const { formattedData = {} } = data || {};

  const updateTime = useCallback(
    ({ newTime, dummy, employee, date, timesheetId, branchId, shouldWaitForBreaktimes }, key) => {
      if (dummy) {
        return api.createJob({
          accountId,
          branchId,
          employeeId: employee.id,
          date,
          timesheet: { [key]: { time: newTime } }
        });
      }
      return api.updateTimeSheet({ accountId, timesheetId, [key]: { time: newTime }, shouldWaitForBreaktimes });
    },
    [accountId]
  );

  const { mutateAsync: createDailyShift } = useOptimisticMutation(
    storeKey,
    ({ employee, date, branchId, hasAddedAnotherShift }) =>
      api.createJob({
        accountId,
        branchId,
        employeeId: employee.id,
        date,
        hasAddedAnotherShift
      }),
    () => {},
    {
      onSuccess: (_, response) => {
        const previousData = queryClient.getQueryData(storeKey);
        const newItem = { ...previousData, jobs: [...previousData.jobs, response?.data] };

        queryClient.setQueryData(storeKey, newItem);
      }
    }
  );

  const mutateTimeSheet = useCallback((previousData, shift, key, value) => {
    const dataKey = shift.isJob ? 'jobs' : shift.isPto ? 'pto' : shift.isLeave ? 'leave' : 'slots';
    let results = [];
    if (shift.dummy) {
      results = [
        ...(previousData[dataKey] || []),
        {
          id: -Date.now(),
          date: shift.date,
          employeeId: shift.employee.id,
          branchId: shift.branchId,
          timesheet: { [key]: value }
        }
      ];
    } else {
      // Optimistically stamp adminApproved so "selectable for processing" flips before the BE response lands.
      const adminApprovedPatch =
        key === 'approved' && value
          ? { adminApproved: { adminId: 0, createdAt: new Date().toISOString() } }
          : key === 'approved' && !value
          ? { adminApproved: null }
          : {};
      results = (previousData[dataKey] || []).map((item) =>
        item.id === shift.id
          ? {
              ...item,
              ...(key === 'approved' ? { isLocked: value } : {}),
              timesheet: {
                ...item.timesheet,
                [key]: value,
                ...adminApprovedPatch,
                reportMethods: withOptimisticAdminReportMethod(item.timesheet?.reportMethods, key),
                hasChangedAfterApprove: key === 'approved' ? false : item.timesheet?.approved
              }
            }
          : item
      );
    }

    return { ...previousData, [dataKey]: results };
  }, []);

  const { mutateAsync: updateStartTime } = useOptimisticMutation(
    storeKey,
    (params) => updateTime({ ...params, shouldWaitForBreaktimes: true }, TIMESHEET_DATA_KEY_OPTIONS.START),
    ({ previousData, newTime, ...shift }) => mutateTimeSheet(previousData, shift, 'startTime', newTime),
    {
      onSuccess: ({ timesheetId, employee: { id: employeeId }, id: slotId }, { data: successData }) => {
        const { startTime, endTime, breaktimes } = successData;

        if (!timesheetId) {
          const previousData = queryClient.getQueryData(storeKey);
          queryClient.setQueryData(storeKey, {
            ...previousData,
            jobs: previousData.jobs.map((job) => (job.id < 0 ? successData : job))
          });
        }

        if (startTime && endTime) {
          refetchPayrollOnSuccess?.({ timesheetId, employeeId });
          queryClient.invalidateQueries(TIMESHEET_PAYROLL_FIELDS_QUERY_KEY);
        }
        refetchProjectOnSuccess?.();

        if (breaktimes) {
          const previousData = queryClient.getQueryData(storeKey);
          const newData = mutateTimeSheet(previousData, { id: slotId }, 'breaktimes', breaktimes);
          queryClient.setQueryData(storeKey, newData);
        }
      },
      refetchOnSuccess: false
    }
  );

  const { mutateAsync: updateCheckInTime } = useOptimisticMutation(
    storeKey,
    (params) => updateTime(params, TIMESHEET_DATA_KEY_OPTIONS.CHECK_IN),
    ({ previousData, newTime, ...shift }) => mutateTimeSheet(previousData, shift, 'checkInTime', newTime),
    { refetchOnSuccess: false, refetchOnError: false }
  );

  const { mutateAsync: updateCheckOutTime } = useOptimisticMutation(
    storeKey,
    (params) => updateTime(params, TIMESHEET_DATA_KEY_OPTIONS.CHECK_OUT),
    ({ previousData, newTime, ...shift }) => mutateTimeSheet(previousData, shift, 'checkOutTime', newTime),
    { refetchOnSuccess: false, refetchOnError: false }
  );

  const { mutateAsync: updateEndTime } = useOptimisticMutation(
    storeKey,
    (params) => updateTime({ ...params, shouldWaitForBreaktimes: true }, TIMESHEET_DATA_KEY_OPTIONS.END),
    ({ previousData, newTime, ...shift }) => mutateTimeSheet(previousData, shift, 'endTime', newTime),
    {
      onSuccess: ({ timesheetId, employee: { id: employeeId }, id: slotId }, { data: successData }) => {
        const { startTime, endTime, breaktimes } = successData;
        if (startTime && endTime) {
          refetchPayrollOnSuccess?.({ timesheetId, employeeId });
          queryClient.invalidateQueries(TIMESHEET_PAYROLL_FIELDS_QUERY_KEY);
        }
        refetchProjectOnSuccess?.();

        if (breaktimes) {
          const previousData = queryClient.getQueryData(storeKey);
          const newData = mutateTimeSheet(previousData, { id: slotId }, 'breaktimes', breaktimes);
          queryClient.setQueryData(storeKey, newData);
        }
      },
      refetchOnSuccess: false
    }
  );

  const { mutateAsync: approveTimeSheet } = useOptimisticMutation(
    storeKey,
    ({ timesheetId, approved }) => api.updateTimeSheet({ accountId, timesheetId, approved: approved ?? true }),
    ({ previousData, ...shift }) => mutateTimeSheet(previousData, shift, 'approved', shift.approved),
    { refetchOnSuccess: false, refetchOnError: false }
  );

  const { mutateAsync: approveAllTimeSheet } = useMutation(({ ids }) => api.approveAll({ accountId, ids }), {
    onSuccess: () => queryClient.invalidateQueries(storeKey, { exact: true })
  });

  const { mutateAsync: deleteTimeSheet } = useOptimisticMutation(
    storeKey,
    ({ id, isJob, branchId, projectId, shiftId, isSoftDelete = true, unbookReasonId }) =>
      isJob
        ? api.deleteJob({ accountId, jobId: id })
        : api.removeSchedule({ accountId, branchId, projectId, shiftId, slotId: id, isSoftDelete, unbookReasonId }),
    ({ previousData, isJob, id }) => {
      if (isJob) {
        return { ...previousData, jobs: previousData.jobs.filter((job) => job.id !== id) };
      }
      return {
        ...previousData,
        slots: previousData.slots.map((slot) =>
          slot.id === id ? { ...slot, timesheet: { ...slot.timesheet, startTime: null, endTime: null } } : slot
        )
      };
    },
    {
      onSuccess: (rest) => queryClient.invalidateQueries(['projects', 'specific', rest?.projectId], { exact: true })
    }
  );

  const { mutateAsync: updateSlotValues } = useOptimisticMutation(
    storeKey,
    ({ key, value, isJob, projectId, shiftId, branchId, id, src, ...slot }) => {
      return !id
        ? api.createJob({ accountId, branchId, [key]: value, ...slot })
        : isJob
        ? api.updateJob({ accountId, jobId: id, [key]: value })
        : api.updateSlot({
            accountId,
            branchId,
            projectId,
            shiftId,
            slotId: id,
            src,
            slot: { [key]: value }
          });
    },
    ({ previousData, key, value, id: slotId, isJob }) => {
      const dataKey = isJob ? 'jobs' : 'slots';

      return {
        ...previousData,
        [dataKey]: previousData[dataKey].map((slot) => (slot.id === slotId ? { ...slot, [key]: value } : slot))
      };
    },
    { refetchOnSuccess: false }
  );

  const { mutateAsync: updateTimesheetTaskField } = useOptimisticMutation(
    storeKey,
    ({ timesheetId, fieldId, value }) => api.updateTimesheetTaskField({ accountId, timesheetId, fieldId, value }),
    ({ previousData, timesheetId, fieldId, value }) => {
      // Both slots (shift workers) and jobs (daily workers) carry shift task (timesheet) fields.
      const patchTimesheetField = (item) => {
        if (item?.timesheet?.id !== timesheetId) {
          return item;
        }

        const existingFields = item.timesheet.timesheetFields || [];
        const hasField = existingFields.some((field) => field.fieldId === fieldId);
        const timesheetFields = hasField
          ? existingFields.map((field) => (field.fieldId === fieldId ? { ...field, value } : field))
          : [...existingFields, { fieldId, value }];

        return { ...item, timesheet: { ...item.timesheet, timesheetFields } };
      };

      return {
        ...previousData,
        slots: (previousData.slots || []).map(patchTimesheetField),
        jobs: (previousData.jobs || []).map(patchTimesheetField)
      };
    },
    // No success refetch: the optimistic value renders correctly on its own, and refetching
    // the whole timesheet on every edit can land mid-flight and drop another in-flight edit.
    { refetchOnSuccess: false }
  );

  const updatePayrollNotes = useCallback(
    (values) => {
      return updateSlotValues({ ...values, src: 'timesheet-updatePayrollNotes', key: SLOT_FIELDS.payrollNotes });
    },
    [updateSlotValues]
  );

  const updateEmployeeRating = useCallback(
    (values) => {
      return updateSlotValues({ ...values, src: 'timesheet-updateEmployeeRating', key: SLOT_FIELDS.rating });
    },
    [updateSlotValues]
  );

  const updateReviewOnEmployee = useCallback(
    (values) => {
      return updateSlotValues({
        ...values,
        src: 'timesheet-updateReviewOnEmployee',
        key: SLOT_FIELDS.reviewOnEmployee
      });
    },
    [updateSlotValues]
  );

  const { mutateAsync: updateShiftDetails } = useMutation(
    ({ shift: { isJob, id, branchId, projectId, shiftId }, values }) =>
      !id
        ? api.createJob({ accountId, branchId, ...values })
        : isJob
        ? api.updateJob({ accountId, jobId: id, ...values })
        : api.updateSlot({
            accountId,
            branchId,
            projectId,
            shiftId,
            slotId: id,
            slot: values,
            src: 'timesheet-updateShiftDetails'
          }),
    {
      onSettled: (_, err, { shift: { timesheetId, employee } }) => {
        const { id: employeeId } = employee;
        queryClient.invalidateQueries(storeKey, { exact: true });
        if (employeeId && timesheetId) {
          refetchPayrollOnSuccess?.({ timesheetId, employeeId });
        }
        refetchProjectOnSuccess?.();
      }
    }
  );

  const { mutateAsync: updateClientRate } = useOptimisticMutation(
    storeKey,
    () => {},
    ({ previousData, slotId, hourlyWage }) => ({
      ...previousData,
      slots: previousData.slots.map((slot) =>
        slot.id === slotId ? { ...slot, clientWage: { ...slot.clientWage, hourlyWage } } : slot
      )
    })
  );

  const { mutateAsync: updateWageValues } = useOptimisticMutation(
    storeKey,
    () => {},
    ({ previousData, slotId, key, value, isLeave }) =>
      isLeave
        ? {
            ...previousData,
            leave: previousData.leave.map((record) =>
              record.id === slotId
                ? { ...record, wageSource: 'manual', wage: { ...record.wage, [key]: value } }
                : record
            )
          }
        : {
            ...previousData,
            slots: previousData.slots.map((slot) =>
              slot.id === slotId ? { ...slot, wage: { ...slot.wage, [key]: value } } : slot
            )
          },
    {
      refetchOnSuccess: false,
      refetchOnError: false
    }
  );

  const { mutateAsync: autofill } = useMutation(
    async ({ rows, override = true }) => {
      const ids = rows.filter(({ timesheetId }) => timesheetId).map(({ timesheetId }) => timesheetId);
      const newJobs = await Promise.all(
        rows
          // Jobs are filled from the branch default job hours - without them there is nothing to fill
          .filter(({ dummy, branchId }) => {
            const { defaultJobStartTime, defaultJobEndTime } = mappedBranches[branchId]?.timesheetConfig || {};
            return dummy && defaultJobStartTime && defaultJobEndTime;
          })
          .map(({ branchId, employee, date }) =>
            api.createJob({
              accountId,
              branchId,
              employeeId: employee.id,
              date
            })
          )
      );

      const allIds = [...ids, ...newJobs.map(({ data: { timesheetId } }) => timesheetId)];

      if (allIds.length) {
        await api.autoFillTimeSheet({ override, accountId, ids: allIds, shouldWaitForBreaktimes: true });
        refetchPayrollOnSuccess?.();
        queryClient.invalidateQueries(TIMESHEET_PAYROLL_FIELDS_QUERY_KEY);
        refetchProjectOnSuccess?.();
      }
    },
    {
      onSuccess: () => queryClient.invalidateQueries(storeKey, { exact: true })
    }
  );

  const { mutateAsync: autoFillCustomHours } = useMutation(
    async ({ startTime, endTime, override, branchId, projectId }) => {
      await api.autoFillProjectTimesheet({
        accountId,
        branchId,
        projectId,
        startTime,
        endTime,
        override,
        shouldWaitForBreaktimes: true
      });
      refetchPayrollOnSuccess?.();
      queryClient.invalidateQueries(TIMESHEET_PAYROLL_FIELDS_QUERY_KEY);
      refetchProjectOnSuccess?.();
    },
    {
      onSuccess: () => queryClient.invalidateQueries(storeKey, { exact: true })
    }
  );

  const { mutateAsync: updateBreak } = useOptimisticMutation(
    storeKey,
    ({ isJob, id, branchId, projectId, shiftId, breakId, startTime, endTime, comments, breakTypeId }) =>
      isJob
        ? api.updateJob({
            accountId,
            jobId: id,
            breaktimes: breakId
              ? { edited: [{ id: breakId, startTime, endTime, comments, breakTypeId }] }
              : { added: [{ startTime, endTime, comments, breakTypeId }] }
          })
        : api.updateSlot({
            accountId,
            branchId,
            projectId,
            shiftId,
            slotId: id,
            src: 'timesheet-updateBreak',
            slot: {
              breaktimes: breakId
                ? { edited: [{ id: breakId, startTime, endTime, comments, breakTypeId }] }
                : { added: [{ startTime, endTime, comments, breakTypeId }] }
            }
          }),
    ({ previousData, isJob, id, breakId, startTime, endTime, comments, breakTypeId }) => {
      const dataKey = isJob ? 'jobs' : 'slots';

      return {
        ...previousData,
        [dataKey]: previousData[dataKey].map((item) =>
          item.id === id
            ? {
                ...item,
                timesheet: {
                  ...item.timesheet,
                  breaktimes: breakId
                    ? (item.timesheet?.breaktimes ?? []).map((breakItem) =>
                        breakItem.id === breakId
                          ? { ...breakItem, startTime, endTime, comments, breakTypeId }
                          : breakItem
                      )
                    : [...(item.timesheet?.breaktimes ?? []), { startTime, endTime, comments, breakTypeId }]
                }
              }
            : item
        )
      };
    },
    {
      onSuccess: ({ timesheetId, breakId }) => {
        refetchPayrollOnSuccess?.({ timesheetId });

        // breakId variable is undefined when creating a new break - in this case, we need to refetch in order for a valid id to be returned
        if (!breakId) {
          queryClient.invalidateQueries(storeKey, { exact: true });
        }
      },
      refetchOnSuccess: false,
      refetchOnError: false
    }
  );

  const { mutateAsync: deleteBreak } = useOptimisticMutation(
    storeKey,
    ({ isJob, id, branchId, projectId, shiftId, breakId }) =>
      isJob
        ? api.updateJob({
            accountId,
            jobId: id,
            breaktimes: { deleted: [breakId] }
          })
        : api.updateSlot({
            accountId,
            branchId,
            projectId,
            shiftId,
            slotId: id,
            src: 'timesheet-deleteBreak',
            slot: {
              breaktimes: { deleted: [breakId] }
            }
          }),
    ({ previousData, isJob, id, breakId }) => {
      const dataKey = isJob ? 'jobs' : 'slots';

      return {
        ...previousData,
        [dataKey]: previousData[dataKey].map((item) =>
          item.id === id
            ? {
                ...item,
                timesheet: {
                  ...item.timesheet,
                  breaktimes: (item.timesheet?.breaktimes ?? []).filter((breakItem) => breakItem.id !== breakId)
                }
              }
            : item
        )
      };
    },
    {
      onSuccess: ({ timesheetId }) => {
        refetchPayrollOnSuccess?.({ timesheetId });
      },
      refetchOnSuccess: false,
      refetchOnError: false
    }
  );

  // Premium client unbooking an external (cross-account) employee. Distinct from deleteTimeSheet's
  // removeSchedule call, which only handles same-account bookings.
  const { mutateAsync: crossAccountUnbookEmployee } = useMutation(
    ({ branchId, projectId, slotId, unbookReasonId, message }) =>
      api.crossAccountUnbookOSSlot({
        accountId,
        branchId,
        projectId,
        slotId,
        unbookReasonId,
        message
      }),
    {
      onSuccess: (_, { projectId }) => {
        queryClient.invalidateQueries(storeKey, { exact: true });
        // Prefix match: the project's key also carries isLite / withPayroll.
        queryClient.invalidateQueries(['projects', 'specific', Number(projectId)]);
        queryClient.invalidateQueries([PROJECT_EXTERNAL_UNBOOKINGS_KEY, accountId, Number(projectId)]);
      }
    }
  );

  return {
    isLoading,
    isFetching,
    data: formattedData,
    refetch,
    updateStartTime,
    updateEndTime,
    updateCheckInTime,
    updateCheckOutTime,
    approveTimeSheet,
    approveAllTimeSheet,
    updateEmployeeRating,
    createDailyShift,
    updateShiftDetails,
    deleteTimeSheet,
    autofill,
    autoFillCustomHours,
    updateReviewOnEmployee,
    updateBreak,
    updatePayrollNotes,
    deleteBreak,
    updateWageValues,
    isBreakTimesDisplayed,
    showEmployeeSignoffColumn,
    showClientSignoffColumn,
    updateClientRate,
    crossAccountUnbookEmployee,
    updateTimesheetTaskField
  };
};

export default useTimeSheet;
